<script lang="ts">
  import './styles.css'
  import { browser } from '$app/environment'
  import { afterNavigate, beforeNavigate } from '$app/navigation'
  import { page } from '$app/stores'
  import { arrow, autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom'
  import {
    ConicGradient,
    Drawer,
    getDrawerStore,
    getModalStore,
    getToastStore,
    initializeStores,
    Modal,
    storePopup,
    Toast,
  } from '@skeletonlabs/skeleton'
  import type { ConicStop } from '@skeletonlabs/skeleton'
  import { QueryClientProvider } from '@tanstack/svelte-query'
  import camelcaseKeys from 'camelcase-keys'
  import posthog from 'posthog-js'
  import { onMount, type Snippet } from 'svelte'

  import CookieBanner from '@components/CookieBanner.svelte'
  import Header from '@components/Header.svelte'
  import MobileNavigationDrawer from '@components/MobileNavigationDrawer.svelte'
  import { huggingFaceTokensQuery } from '@routes/huggingface-tokens/huggingface-tokens-store'
  import { modalRegistry } from '@src/utils/modal-registry.svelte'
  import { PRODUCT_URLS, PRODUCTS } from '@src/utils/product'
  import { loggedInStore, User } from '@stores/logged-in-store'
  import { themeStore } from '@stores/theme-store'
  import {
    checkAuth,
    checkWorkOSAuth,
    fakeLogin,
    forceFirebaseLogout,
    forceLogout,
    getAuthProvider,
    PRE_AUTH_ROUTES,
    setActiveAuthProvider,
  } from '@utils/auth'
  import { type Config, configureFirebase, getConfig } from '@utils/config.svelte'
  import { queryClient } from '@utils/svelte-query-client'

  interface Props {
    children?: Snippet
  }

  let { children }: Props = $props()

  initializeStores()
  storePopup.set({ computePosition, autoUpdate, offset, shift, flip, arrow })

  let initialized = $state(false)
  let config: Config | undefined = $state()

  const toastStore = getToastStore()
  const modalStore = getModalStore()
  const drawerStore = getDrawerStore()

  onMount(() => {
    const destroyTheme = themeStore.initialize()
    return () => {
      destroyTheme?.()
    }
  })

  onMount(async () => {
    config = await getConfig(toastStore)
    setActiveAuthProvider(getAuthProvider(config))
    window.airGapped = config.airGapped
    if (!config.fakeLogin && !config.airGapped && !config.workosEnabled) {
      configureFirebase(config)
    }

    initialized = false
    const url = $page.url
    if (
      PRE_AUTH_ROUTES.includes(url.pathname) &&
      !url.searchParams.has('ui_sid') &&
      !config.fakeLogin
    ) {
      await forceLogout(false, getAuthProvider(config))
      initialized = true
      return
    }
    const authUserString = localStorage.getItem('authUser')
    const prefetchHuggingFaceTokens = async () => {
      await queryClient.prefetchQuery(huggingFaceTokensQuery())
    }
    if (config.fakeLogin) {
      try {
        await fakeLogin(toastStore)
        await prefetchHuggingFaceTokens()
      } catch (error) {
        console.error('Failed to fake login', error)
        await forceFirebaseLogout()
      }
      modalStore.clear()
    } else if (authUserString) {
      const authUser = JSON.parse(authUserString)
      let authRestored = true
      if (config.workosEnabled) {
        authRestored = await checkWorkOSAuth(toastStore)
      } else if (!config.airGapped) {
        await checkAuth(toastStore) // <- Try to refresh the user's auth token
        authRestored = Boolean(localStorage.getItem('authUser'))
      }
      // Only air-gapped auth is restored directly from localStorage. WorkOS and Firebase
      // refresh their store state from server/session truth above.
      if (config.airGapped) {
        loggedInStore.update(User.login(authUser.email, camelcaseKeys(authUser.loginResult)))
      }
      if (!authRestored) {
        initialized = true
        return
      }
      await prefetchHuggingFaceTokens()
      modalStore.clear()
    } else if (!url.searchParams.has('ui_sid')) {
      if (config.workosEnabled) {
        // No localStorage auth but WorkOS may have set a session cookie during the OAuth callback.
        // Try to restore it here to avoid an unnecessary /login redirect.
        await checkWorkOSAuth(toastStore)
      } else {
        await forceLogout() // No local auth and no WorkOS session to restore; send the user to login.
      }
    }
    initialized = true
  })

  function captureIfEnabled(eventName: string, props?: Record<string, unknown>) {
    type PosthogWindow = Window & { __posthog_enabled?: boolean }
    const w = window as PosthogWindow
    if (browser && w.__posthog_enabled) {
      posthog.capture(eventName, props)
    }
  }

  if (browser) {
    beforeNavigate(() => {
      captureIfEnabled('$pageleave')
      if ($drawerStore.id === 'mobile-nav' && $drawerStore.open) {
        drawerStore.close()
      }
    })
    afterNavigate(() => captureIfEnabled('$pageview', { version: VERSION }))
  }

  const products = $derived(
    PRODUCTS.filter((product) => {
      return PRODUCT_URLS[product].some((url) => $page.url.pathname.startsWith(url))
    }),
  )

  const configLoading = $derived(!config)

  const productActive = $derived(
    products && products.some((product) => config?.activeProducts?.includes(product)),
  )

  const conicStops = [
    { color: 'transparent', start: 0, end: 25 },
    { color: 'rgb(var(--color-primary-500))', start: 75, end: 100 },
  ] satisfies ConicStop[]
</script>

{#snippet loadingState()}
  <div
    class="flex min-h-screen flex-col items-center justify-center gap-3 text-surface-700 dark:text-surface-200"
  >
    <ConicGradient
      stops={conicStops}
      spin
      width="w-16"
      regionCaption="text-base font-semibold text-surface-700 dark:text-surface-200"
    >
      Loading...
    </ConicGradient>
  </div>
{/snippet}

<svelte:head>
  {#if !config?.airGapped && !config?.fakeLogin && !config?.workosEnabled}
    <link
      type="text/css"
      rel="stylesheet"
      href="https://www.gstatic.com/firebasejs/ui/6.0.1/firebase-ui-auth.css"
    />
  {/if}
</svelte:head>

<div class="app overflow-auto">
  {#if initialized}
    <Drawer>
      {#if $drawerStore.id === 'mobile-nav'}
        <MobileNavigationDrawer />
      {/if}
    </Drawer>
    <Header />

    <QueryClientProvider client={queryClient}>
      <main>
        {#if configLoading}
          {@render loadingState()}
        {:else if !products || products.length === 0 || (products && productActive)}
          {@render children?.()}
        {:else}
          <h1>Product not available</h1>
          <p class="mt-4">
            You have not purchased this product. Please contact Hirundo to buy this product.
          </p>
        {/if}
      </main>
      {#if $loggedInStore.loggedIn}
        <Modal components={modalRegistry} buttonPositive="variant-filled-primary" />
      {/if}
      <Toast position="tr" zIndex="z-[1000]" width="max-w-[640px] [word-break:break-word]" />
    </QueryClientProvider>

    <footer>
      <p>
        Visit <a href="https://www.hirundo.io/blog">hirundo.io</a> to read our latest blog posts
      </p>
    </footer>

    <!-- GDPR Cookie Banner -->
    <CookieBanner postHogId={config?.postHogId} visible={true} />
  {:else}
    {@render loadingState()}
  {/if}
</div>

<style>
  .app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
    position: relative;
    z-index: 0;
  }

  footer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 12px;
  }

  footer a {
    font-weight: bold;
  }

  @media (min-width: 480px) {
    footer {
      padding: 12px 0;
    }
  }
</style>
